import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  CheckboxField,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import {
  PlayerUpdateGamanzaStatusMutation,
  PlayerUpdateGamanzaStatusMutationVariables,
} from './__generated__/PlayerUpdateGamanzaStatusForm';

const query = graphql`
  query SanityPlayerUpdateGamanzaStatusForm {
    sanityPlayerUpdateGamanzaStatusForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const updatePlayerGamanzaStatusMutation = gql`
  mutation PlayerUpdateGamanzaStatus($playerId: ID!, $blockPlayer: Boolean!) {
    updateGamanzaBlockStatusByPlayerId: updateGamanzaBlockStatusByPlayerIdV2(
      playerId: $playerId
      blockPlayer: $blockPlayer
    ) {
      getGamanzaPlayerBlockStatus {
        isBlocked
      }
    }
  }
`;

const PlayerUpdateGamanzaStatusForm: FC<{
  playerId: PlayerId;
  blockPlayer: boolean;
}> = ({ playerId, blockPlayer }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerUpdateGamanzaStatusFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const form = staticData.sanityPlayerUpdateGamanzaStatusForm;
  const { close } = useModalContext();
  const { t } = useTranslate();

  const [updateGamanzaBlockStatusState, updateGamanzaBlockStatus] = useMutation<
    PlayerUpdateGamanzaStatusMutation,
    PlayerUpdateGamanzaStatusMutationVariables
  >(updatePlayerGamanzaStatusMutation);

  const defaultValues = {
    blockPlayer,
  };

  const onSubmit = ({ blockPlayer }: typeof defaultValues) => {
    updateGamanzaBlockStatus({
      playerId,
      blockPlayer,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <Form
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <CheckboxField
              title="Is player blocked in Gamanza?"
              name="blockPlayer"
              id="PlayerUpdateGamanzaStatusForm_blockPlayer"
              className="col-span-full"
              defaultChecked={blockPlayer}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value="Update"
              disabled={updateGamanzaBlockStatusState.fetching}
            />
          </Form>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerUpdateGamanzaStatusForm;
